import {
  favoriteProsCreate,
  favoriteProsDestroy,
  type FindPro,
  getNextPageParam,
  stafferGigsterProfilesSearchList,
  type StafferGigsterProfilesSearchListParams,
} from "@sidegigllc/api/client";
import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  useMutation,
} from "@tanstack/react-query";
import type { PaginationOptions } from "./types";

const sortOptionMapping = {
  completion_rate: "-review_summary__completion_percentage",
  rating: "-review_summary__avg_rating",
  gigs_worked: "-review_summary__gigs_worked",
} as const;

const friendlySortKeys = ["rating", "completion_rate", "gigs_worked"] as const;

export type SortOption = (typeof friendlySortKeys)[number];
export const sortOptionsLabelLookup: Record<SortOption, string> = {
  completion_rate: "Completion Rate",
  rating: "Rating",
  gigs_worked: "Shifts worked",
};

export type FindAProQueryParameters = Pick<PaginationOptions, "page_size"> & {
  ordering?: SortOption;
  is_verified?: boolean;
  has_worked_together?: boolean;
  is_new_pro?: boolean;
  market_id?: number;
  location_id?: number;
  rating_gte?: number;
  certifications?: Array<number>;
  pro_certifications?: Array<string>;
  previous_positions?: Array<number>;
  pro_fullname?: string;
  badge_types?: Array<string>;
};

// Page 1 receives the friendly params (arrays); subsequent pages come from
// `getNextPageParam` parsing the `next` URL, so values arrive already
// wire-encoded (comma-joined strings). Keep this idempotent for both.
const join = (values?: Array<number | string> | string | null): string | undefined => {
  if (Array.isArray(values)) return values.length ? values.join(",") : undefined;
  return values ?? undefined;
};

const toSearchParams = (
  params: FindAProQueryParameters & { page?: number },
): StafferGigsterProfilesSearchListParams => ({
  page: params.page,
  page_size: params.page_size,
  ordering: params.ordering ? (sortOptionMapping[params.ordering] ?? params.ordering) : undefined,
  is_verified: params.is_verified,
  has_worked_together: params.has_worked_together,
  is_new_pro: params.is_new_pro,
  market_id: params.market_id,
  location_id: params.location_id,
  rating_gte: params.rating_gte,
  pro_fullname: params.pro_fullname,
  certifications: join(params.certifications),
  pro_certifications: join(params.pro_certifications),
  previous_positions: join(params.previous_positions),
  badge_types: join(params.badge_types),
});

type FindAProPage = Awaited<ReturnType<typeof stafferGigsterProfilesSearchList>>;
type FindAProProsOptions = UseInfiniteQueryOptions<
  FindAProPage,
  unknown,
  InfiniteData<FindAProPage, number>,
  Array<string | undefined | FindAProQueryParameters>,
  number
>;

const QK_FIND_A_PRO_LIST = "FIND_A_PRO_LIST";
export const findAProQueryOptions = (params?: FindAProQueryParameters) =>
  ({
    queryKey: [QK_FIND_A_PRO_LIST, params],
    queryFn: ({ pageParam }) => stafferGigsterProfilesSearchList(toSearchParams({ ...params, page: pageParam })),
    initialPageParam: 1,
    getNextPageParam,
  }) satisfies FindAProProsOptions;

export type FindAProPro = FindPro & { hasGroup: boolean };

export type ToggleFavoriteParams =
  | { favorite: true; locationId: number; userId: number }
  | { favorite: false; favoriteId: number };

export const useToggleFavorite = (
  options?: Omit<UseMutationOptions<void, unknown, ToggleFavoriteParams>, "mutationKey" | "mutationFn">,
) =>
  useMutation({
    mutationKey: ["toggleFavorite"],
    mutationFn: async (options: ToggleFavoriteParams) => {
      if (options.favorite) {
        await favoriteProsCreate({ location_id: options.locationId, pro_id: options.userId });
      } else {
        await favoriteProsDestroy(options.favoriteId);
      }
    },
    ...options,
  });
